import { ActivatedRouteSnapshot, CanActivateChildFn, CanActivateFn, GuardResult, Params } from '@angular/router';
import { ClientAuthService } from '../client-auth.service';
import { inject } from '@angular/core';
import { DashboardUserRolesService } from '@api-services/dashboard-user-roles.service';
import { DashboardLocalStorageService, settingsKey } from '@services/dashboard-localstorage.service';

/**
 * Collect all route parameters from ancestors. This has the dashboardId somewhere in the
 * ancestry tree.
 */
function getFullRouterParams(rootSnapshot: ActivatedRouteSnapshot): Params {
  let params = {};
  const stack: ActivatedRouteSnapshot[] = [rootSnapshot];
  while (stack.length > 0) {
    const route = stack.pop()!;
    params = {...params, ...route.params};
    stack.push(...route.children);
  }
  return params;
}

/**
 * This should always return true, as the goal is to try and load the client's dashboard role.
 * Not to restrict them in any way. That is up to a separate guard or check.
 */
async function canActivate(route: ActivatedRouteSnapshot): Promise<GuardResult> {
  const clientAuthService = inject(ClientAuthService);
  const dashboardUserRolesService = inject(DashboardUserRolesService);
  const dashboardLocalStorageService = inject(DashboardLocalStorageService);
  const routeParams = getFullRouterParams(route.root);
  const dashboardIdFromRoute = parseInt(routeParams["dashboardId"] ?? "");
  let dashboardId: number;

  if (isNaN(dashboardIdFromRoute)) {
    // Try to get the last dashboard from the local storage
    // This happens when they go from viewing a dashboard to, possibly, their Footbridge billing page
    const dashboardIdFromLocalStorage = dashboardLocalStorageService.Get(settingsKey.LastViewedDashboardId);

    if (dashboardIdFromLocalStorage === null || isNaN(dashboardIdFromLocalStorage)){
      // No dashboard at all, so no role
      clientAuthService.CurrentClientDashboardRole.set(null);
      return true;
    } else {
      dashboardId = dashboardIdFromLocalStorage;
    }
  } else {
    dashboardId = dashboardIdFromRoute;
  }

  return new Promise<boolean>(resolve => {
    dashboardUserRolesService.GetRoleForCurrentAccount(dashboardId).subscribe({
      next: apiResponse => {
        const role = apiResponse.Data;
        clientAuthService.CurrentClientDashboardRole.set(role);
        return resolve(true);
      },
      error: () => {
        clientAuthService.CurrentClientDashboardRole.set(null);
        return resolve(true);
      }
    });
  });
};

export const loadClientDashboardRoleChildGuard: CanActivateChildFn = async (route) => {
  return await canActivate(route);
};

export const loadClientDashboardRoleGuard: CanActivateFn = async (route) => {
  return await canActivate(route);
};